import React from 'react';
import { useObservable } from 'react-use-observable';

import { Avatar, Button, Pagination, Rate, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import Truncate from 'components/Truncate';
import { EChargeType } from 'interfaces/IEditProduct';
import PaymentStatusTag from 'v2/components/Shared/PaymentStatusTag';
import PaymentTypeImg from 'v2/components/Shared/PaymentTypeImg';
import { EPaymentMethod, type ShoppingDelivery } from 'v2/interfaces/shopping';
import shoppingService from 'v2/services/shopping/product';
import tableService from 'v2/services/table';

import { Icon } from '@eduzz/ui-antd-theme';

import ProductReview from '../ProductReview';

type ProductListProps = {
  handleReloadProducts: () => void;
  onClickRow: (shopping: ShoppingDelivery) => void;
};

type UnprocessedReview = {
  invoiceId: number;
  productId: number;
  rating: number;
  comment: string | null;
};

const ProductList = ({ handleReloadProducts, onClickRow }: ProductListProps) => {
  const [isLoading] = useObservable(() => tableService.isLoading(), []);
  const [productWillBeReviewd, setProductWillBeReviewd] = React.useState<ShoppingDelivery | null>(null);
  const [openProductReview, setOpenProductReview] = React.useState(false);

  const [invoicesList] = useObservable(() => {
    return shoppingService.list();
  }, []);

  const [pagination] = useObservable(() => {
    return shoppingService.getPaginationResponse();
  }, []);

  function setProductRating(invoice: ShoppingDelivery) {
    setProductWillBeReviewd(invoice);
    setOpenProductReview(true);
  }

  function onChangePage(page: number, pageSize: number) {
    shoppingService.setPagination({ current_page: page, per_page: pageSize });
  }

  function getPaymentMethodName(invoice: ShoppingDelivery) {
    if (invoice.payment_method.id === EPaymentMethod.PIX_AUTOMATICO) return invoice.payment_method.method;
    if (invoice.charge_type === EChargeType.FREE) return 'Gratuito';
    if (invoice.payment_method.id) return invoice.payment_method.name;
    return 'Desconhecido';
  }

  function getProductRating(invoice: ShoppingDelivery) {
    const hasReview = typeof invoice.product.review?.rating === 'number';
    const isPaid = invoice.payment_status.alias === 'paid';

    const unprocessedReviews: UnprocessedReview[] = JSON.parse(localStorage.getItem('unprocessed-reviews')) || [];
    const unprocessedReview =
      unprocessedReviews.filter(
        review => review.invoiceId === invoice.invoice_id && review.productId === invoice.product.id
      )[0] || null;

    const productReviewedButNotProcessed = !hasReview && !!unprocessedReview;
    const ratingValue = productReviewedButNotProcessed ? unprocessedReview.rating : invoice.product.review?.rating;

    if (hasReview && !!unprocessedReview) {
      const newUnprocessedReviews = unprocessedReviews.filter(
        review => review.invoiceId !== invoice.invoice_id && review.productId !== invoice.product.id
      );
      localStorage.setItem('unprocessed-reviews', JSON.stringify(newUnprocessedReviews));
    }

    if (!isPaid) return null;

    if (!hasReview && !unprocessedReview) {
      return (
        <Typography.Link
          onClick={e => {
            e.stopPropagation();
            setProductRating(invoice);
          }}
        >
          Avaliar produto
        </Typography.Link>
      );
    }

    return <Rate value={ratingValue} disabled />;
  }

  const columns: ColumnsType<ShoppingDelivery> = [
    {
      title: 'Produto',
      key: 'product',
      width: 240,
      render: (_, delivery) => (
        <div className='flex items-center gap-3'>
          <Avatar shape='square' size={40} src={delivery.product.image} className='shrink-0' />
          <div className='min-w-0'>
            <Typography.Text className='block'>
              <Truncate count={70} final='...' title={delivery.product.title}>
                {delivery.product.title}
              </Truncate>
            </Typography.Text>
            <Typography.Text type='secondary' className='block'>
              {delivery.producer.email || '-'}
            </Typography.Text>
          </div>
        </div>
      )
    },
    {
      title: 'Tipo',
      key: 'type',
      width: 120,
      render: (_, delivery) => <Typography.Text>{delivery.product.type}</Typography.Text>
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, delivery) => (
        <div className='flex flex-col items-start gap-1'>
          <PaymentStatusTag delivery={delivery} />
          {delivery.order?.status ? (
            <Tag className='-me-2' color='blue'>
              {delivery.order.status}
            </Tag>
          ) : null}
        </div>
      )
    },
    {
      title: 'Forma de pagamento',
      key: 'paymentMethod',
      width: 180,
      onHeaderCell: () => ({ className: 'whitespace-nowrap' }),
      render: (_, delivery) => (
        <div className='flex items-center gap-2'>
          <PaymentTypeImg delivery={delivery} />
          <Typography.Text>{getPaymentMethodName(delivery)}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Avaliação',
      key: 'rating',
      width: 140,
      render: (_, delivery) => getProductRating(delivery)
    },
    {
      key: 'actions',
      width: 48,
      align: 'center',
      render: (_, delivery) => (
        <Button
          id='btn-options-shopping-table-row'
          type='text'
          shape='circle'
          icon={<Icon.Ellipsis size={24} />}
          aria-label='Ver detalhes da compra'
          onClick={() => onClickRow(delivery)}
        />
      )
    }
  ];

  const paginationConfig = pagination
    ? {
        current: pagination.current_page || 1,
        pageSize: pagination.per_page || 10,
        total: pagination.total_items || 0,
        pageSizeOptions: [10, 20, 50, 100],
        showSizeChanger: true,
        onChange: onChangePage,
        showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} de ${total}`,
        responsive: true
      }
    : false;

  const dataSource = invoicesList || [];

  return (
    <>
      <Table<ShoppingDelivery>
        id='products-table'
        columns={columns}
        dataSource={dataSource}
        rowKey='invoice_id'
        loading={isLoading}
        locale={{ emptyText: 'Nenhum registro encontrado.' }}
        pagination={false}
        size='small'
        sticky
        scroll={{ x: 'max-content' }}
      />
      {paginationConfig && (
        <div className='mt-4 flex justify-end'>
          <Pagination {...paginationConfig} />
        </div>
      )}

      {productWillBeReviewd && (
        <ProductReview
          invoice={productWillBeReviewd}
          product={productWillBeReviewd.product}
          open={openProductReview}
          onClose={() => setOpenProductReview(false)}
          handleReloadProducts={handleReloadProducts}
        />
      )}
    </>
  );
};

export default ProductList;
